import { Fragment, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useSettings } from "../../../../../context/settings";
import Drawer from "react-modern-drawer";
import { toast } from "react-toastify";
import DOC_STYLES from "../../../../Sales/utils/docStyles";
import { IoCheckmarkDoneCircleOutline, IoClose, IoCopyOutline, IoCreateOutline, IoPrintOutline, IoTrashOutline } from "react-icons/io5";
import { Badge, Dropdown, DropdownItem, DropdownMenu, DropdownToggle, Spinner } from "reactstrap";
import { get, post } from "../../../../../utils/helpers/api";
import DefaultButton from "../../../../../components/Buttons/Default";
import { PiDotsThreeOutlineVerticalFill } from "react-icons/pi";
import { printOrderStatus } from "../../../utils/helpers";
import { printSlug } from "../../../../../utils/helpers/text";
import { AWS_S3_URL } from "../../../../../utils/constants";
import LegalIdentifierLines from "../../../../../components/Documents/LegalIdentifierLines";
import { amountInWords } from "../../../../../utils/helpers/numberToWords";

const PRIMARY = "#6C63FF";

function fmt(n) {
  return parseFloat(n ?? 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export default function ViewDrawer({ orderId, isOpen, onClose, onDelete, refreshOrders }) {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { formatDate } = useSettings();
  const [order, setOrder] = useState(null);
  const [companyData, setCompanyData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);

  const toggle = () => setDropdownOpen((prevState) => !prevState);

  const getOrder = async () => {
    if (!orderId) return;

    try {
      const data = await get(`/purchases/orders/view/${orderId}`);
      setOrder(data.order ?? data.data ?? null);
      setCompanyData(data.company ?? null);
    } catch (error) {
      toast.error(t('purchases.failedLoadOrder'));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (!orderId) return;
    setLoading(true);
    setOrder(null);
    getOrder();
  }, [orderId]);

  const currency = (order?.currency ?? "usd").toUpperCase();

  const handlePrint   = () => window.print();
  const handleEdit    = () => { onClose(); navigate(`/purchases/purchase-orders/edit/${order.id}`, { state: { orderData: { ...order } } }); };

  const markAs = async (status) => {
    try {
      await post(`/purchases/orders/mark-as-${status}/${order.id}`);
      toast.success(t('purchases.orderMarkedAs', { status: t(`common.${status}`) }));
      refreshOrders();
      getOrder();
    } catch (error) {
      toast.error(t('purchases.failedUpdateOrderStatus'));
    }
  };

  const clone = async () => {
    try {
      const data = await post(`/purchases/orders/clone/${order.id}`);
      toast.success(t('purchases.orderCloned'));
      onClose();
      navigate(`/purchases/purchase-orders/edit/${data.order.id}`, { state: { orderData: data.order } });
    } catch (error) {
      toast.error(t('purchases.failedCloneOrder'));
    }
  };

  const orderLabel = order ? `${t('purchases.orderHash')}${order.order_number}` : t('common.loading');

  return(
    <Drawer
      open={isOpen}
      onClose={onClose}
      direction="right"
      size={680}
      enableOverlay
      lockScroll
      zIndex={9000}
      duration={200}
    >
      <div style={DOC_STYLES.wrap}>
        {/* ── Header ── */}
        <div style={DOC_STYLES.header}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <button
              style={{ background: "none", border: "none", cursor: "pointer", color: "#888", padding: 4, borderRadius: 6, display: "flex", alignItems: "center" }}
              onClick={onClose}
              onMouseEnter={e => { e.currentTarget.style.background = "#f0f0f0"; e.currentTarget.style.color = "#333"; }}
              onMouseLeave={e => { e.currentTarget.style.background = "none"; e.currentTarget.style.color = "#888"; }}
            >
              <IoClose size={20} />
            </button>
            <span style={{ fontSize: 14, fontWeight: 700, color: "#1d1d1f" }}>{orderLabel}</span>
            {order &&
              printOrderStatus(order.status)
            }
          </div>
        </div>

        {/* ── Action Bar ── */}
        <div style={DOC_STYLES.actionBar}>
          {order?.status !== 'received' && order?.status !== 'canceled' && (
            <DefaultButton className="btn-action" size="sm" onClick={handleEdit} disabled={!order} color="light" outline>
              <IoCreateOutline size={15} />
              {t('common.edit')}
            </DefaultButton>
          )}
          <DefaultButton className="btn-action" size="sm" onClick={handlePrint} disabled={!order} color="light" outline>
            <IoPrintOutline size={15} />
            {t('common.print')}
          </DefaultButton>
          <Dropdown isOpen={dropdownOpen} toggle={toggle} direction="down" size="sm">
            <DropdownToggle outline color="light" className="btn-action">
              {t('common.more')} <PiDotsThreeOutlineVerticalFill />
            </DropdownToggle>
            <DropdownMenu end>
              {order?.status === 'draft' && (
                <DropdownItem onClick={() => markAs("sent")}>
                  <IoCheckmarkDoneCircleOutline size={16} /> {t('purchases.markAsSent')}
                </DropdownItem>
              )}
              {order?.status === 'sent' && (
                <DropdownItem onClick={() => markAs("approved")}>
                  <IoCheckmarkDoneCircleOutline size={16} /> {t('purchases.markAsApproved')}
                </DropdownItem>
              )}
              {order?.status === 'approved' && (
                <DropdownItem onClick={() => markAs("received")}>
                  <IoCheckmarkDoneCircleOutline size={16} /> {t('purchases.markAsReceived')}
                </DropdownItem>
              )}
              {order?.status !== 'canceled' && order?.status !== 'received' && (
                <DropdownItem onClick={() => markAs("canceled")}>
                  <IoCheckmarkDoneCircleOutline size={16} /> {t('purchases.markAsCanceled')}
                </DropdownItem>
              )}
              <DropdownItem onClick={clone} disabled={!order}>
                <IoCopyOutline size={16} /> {t('sales.clone')}
              </DropdownItem>
              {order?.status !== 'received' && order?.status !== 'canceled' && (
                <Fragment>
                  <DropdownItem divider />
                  <DropdownItem className="text-danger" onClick={onDelete} disabled={!order}>
                    <IoTrashOutline size={16} /> {t('common.delete')}
                  </DropdownItem>
                </Fragment>
              )}
            </DropdownMenu>
          </Dropdown>
        </div>

        {/* ── Scrollable document area ── */}
        <div style={DOC_STYLES.scroll}>
          {loading && (
            <div className="d-flex justify-content-center align-items-center" style={{ height: 200 }}>
              <Spinner color="secondary" />
            </div>
          )}

          {!loading && order && (
            <div id="quote-print-area" style={DOC_STYLES.paper}>
              {/* Company + Order Title */}
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 36 }}>
                <div>
                  <img 
                    src={companyData?.logo_path ? AWS_S3_URL + companyData.logo_path : require('../../../../../assets/images/brand.svg').default} 
                    height={38} 
                    style={{ marginBottom: 10 }}
                    alt={t('settings.logo')}
                  />
                  <div style={{ fontWeight: 800, fontSize: 18, color: "#1d1d1f", lineHeight: 1.2 }}>
                    { companyData?.legal_name || t('common.yourCompanyName') }
                  </div>
                  <div style={{ fontSize: 12, color: "#777", marginTop: 6, lineHeight: 1.8 }}>
                    { companyData?.street_address || t('common.companyStreetAddress') }<br />
                    { companyData?.city || t('settings.city') } { companyData?.state || t('common.state') } { companyData?.postal_code || t('settings.postalCode') }<br />
                    { printSlug(companyData?.country) || t('common.country') }<br />
                    { companyData?.email || t('common.emailExample') }
                  </div>
                  <LegalIdentifierLines party={companyData} compact />
                </div>
                <div style={{ textAlign: "right" }}>
                  <div style={{ fontWeight: 800, fontSize: 30, color: PRIMARY, letterSpacing: 3, lineHeight: 1, textTransform: "uppercase" }}>
                    {t('purchases.purchaseOrder')}
                  </div>
                  <div style={{ color: "#444", fontSize: 14, marginTop: 6, fontWeight: 600 }}>
                   <b>{t('common.date')}: </b> { formatDate(order.order_date) }
                  </div>
                  <div style={{ color: "#888", fontSize: 14, marginTop: 6, fontWeight: 600 }}>
                    #{order.order_number || order.id}
                  </div>
                </div>
              </div>

              <hr style={DOC_STYLES.divider} />

              {/* Bill To + Meta */}
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 32 }}>
                <div style={{ flex: 1, marginRight: 20 }}>
                  <div style={DOC_STYLES.label}>{t('sales.vendor')}</div>
                  <div style={{ fontWeight: 700, fontSize: 15, color: "#1d1d1f" }}>{order.supplier.company_name}</div>
                  <div style={{ fontSize: 12, color: "#777", marginTop: 6, lineHeight: 1.8 }}>
                    { order.supplier.address_line_1 }<br />
                    { order.supplier.city }, { order.supplier.state } { order.supplier.postal_code }<br />
                    { order.supplier.country }
                  </div>
                  <LegalIdentifierLines party={order.supplier} compact />
                </div>
                <div style={{ flex: 1 }}>
                  <div style={DOC_STYLES.label}>{t('sales.shipTo')}</div>
                  <div style={{ fontWeight: 700, fontSize: 15, color: "#1d1d1f" }}>
                    { companyData?.legal_name || t('common.yourCompanyName') }
                  </div>
                  <div style={{ fontSize: 12, color: "#777", marginTop: 6, lineHeight: 1.8 }}>
                    { companyData?.street_address || t('common.companyStreetAddress') }<br />
                    { companyData?.city || t('common.companyCity') }, { companyData?.state || t('common.companyState') } { companyData?.postal_code || t('common.companyPostalCode') }<br />
                    { companyData?.country || t('common.companyCountry') }
                  </div>
                </div>
              </div>

              {order.notes && (
                <div style={{ marginBottom: 24, fontSize: 13, background: "#f9f9ff", border: "1px solid #ede9ff", borderRadius: 7, padding: "10px 14px", color: "#444" }}>
                  <strong style={{ color: PRIMARY }}>{t('purchases.notesPrefix')}</strong>&nbsp;{order.notes}
                </div>
              )}

              {/* Items table */}
              <table style={DOC_STYLES.itemsTable}>
                <thead>
                  <tr>
                    <th style={{ ...DOC_STYLES.th, width: 36, borderRadius: "6px 0 0 0" }}>#</th>
                    <th style={DOC_STYLES.th}>{t('common.itemDescription')}</th>
                    <th style={{ ...DOC_STYLES.thRight, width: 60 }}>{t('common.qty')}</th>
                    <th style={{ ...DOC_STYLES.thRight, width: 100 }}>{t('common.unitPrice')}</th>
                    <th style={{ ...DOC_STYLES.thRight, width: 100, borderRadius: "0 6px 0 0" }}>{t('common.total')}</th>
                  </tr>
                </thead>
                <tbody>
                  {order.items.length === 0 ? (
                    <tr>
                      <td colSpan={5} style={{ ...DOC_STYLES.td, textAlign: "center", color: "#aaa", padding: "32px 0" }}>{t('common.noItemsAdded')}</td>
                    </tr>
                  ) : (
                    order.items.map((item, i) => (
                      <tr key={item.id ?? i} style={{ background: i % 2 === 0 ? "#fff" : "#fafafa" }}>
                        <td style={DOC_STYLES.td}>{i + 1}</td>
                        <td style={DOC_STYLES.td}>
                          <span style={{ fontWeight: 600 }}>{item.product_name || "—"}</span>
                          {item.description && (
                            <div style={{ color: "#888", fontSize: 12, marginTop: 2 }}>{item.description}</div>
                          )}
                        </td>
                        <td style={DOC_STYLES.tdRight}>{item.quantity}</td>
                        <td style={DOC_STYLES.tdRight}>{fmt(item.unit_price)}</td>
                        <td style={DOC_STYLES.tdRight}>{fmt(item.total_price)}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>

              {/* Totals */}
              <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 20 }}>
                <div style={DOC_STYLES.totalsBox}>
                  <div style={DOC_STYLES.totalsLine}>
                    <span>{t('common.subTotal')}</span>
                    <span style={{ fontWeight: 500 }}>{fmt(order?.sub_total)} {currency}</span>
                  </div>
                  {order.discount_amount > 0 && (
                    <div style={DOC_STYLES.totalsLine}>
                      <span>{t('sales.discount')}</span>
                      <span style={{ fontWeight: 500, color: "red" }}>- {fmt(order?.discount_amount ?? 0)} {currency}</span>
                    </div>
                  )}
                  {order.tax_amount > 0 && (
                    <div style={DOC_STYLES.totalsLine}>
                      <span>{t('sales.taxWithRate', { rate: order.tax_rate ?? 0 })}</span>
                      <span style={{ fontWeight: 500 }}>{fmt(order?.tax_amount ?? 0)} {currency}</span>
                    </div>
                  )}
                  <div style={DOC_STYLES.totalsBold}>
                    <span>{t('common.total')}</span>
                    <span style={{ color: PRIMARY }}>{fmt(order?.total_amount)} {currency}</span>
                  </div>
                </div>
              </div>

              {/* Amount in words */}
              <div style={{ marginTop: 20, paddingTop: 14, borderTop: "1px dashed #ddd", fontSize: 12.5, color: "#444" }}>
                <span style={{ color: "#888" }}>{t('legal.amountInWords')} </span>
                <span style={{ fontWeight: 600, textTransform: "capitalize" }}>
                  {amountInWords(order?.total_amount, i18n.language, order?.currency)}
                </span>
              </div>
            </div>
          )}
        </div>
      </div>
    </Drawer>
  );
}
